import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefLabel } from '@/components/refLabel';
import { Icon } from '@iconify/react';
import { ProgressBar } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import type { BranchComparison } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    clampPercent,
    getBranchColor,
    showsShareOfTop,
    subtitleKeyFor,
} from './branchComparisonMeta';
import { formatGel } from '@/utils/money';

type BranchComparisonViewProps = {
    data: BranchComparison;
};

export const BranchComparisonView: React.FC<BranchComparisonViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.branchComparison.title'),
            subtitle: t(subtitleKeyFor(data.branches.length)),
            shareAria: t('sales.widgets.branchComparison.shareAria'),
        }),
        [data.branches.length, t],
    );

    const showsShare = showsShareOfTop(data.branches.length);

    return (
        <section
            aria-labelledby="branch-comparison-widget-title"
            {...getNodeTestIdProp('branch-comparison-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('branch-comparison-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('branch-comparison-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('branch-comparison-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp(
                                'branch-comparison-icon-wrap',
                            )}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:shop-2-bold-duotone"
                                {...getNodeTestIdProp('branch-comparison-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'branch-comparison-widget-subtitle',
                        )}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content>
                    <ul
                        aria-label={copy.title}
                        className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
                        {...getNodeTestIdProp('branch-comparison-legend')}
                    >
                        {data.branches.map((branch, index) => {
                            const percentRounded = Math.round(
                                clampPercent(branch.percentageOfTop),
                            );
                            const branchColor = getBranchColor(index);
                            return (
                                <li
                                    key={branch.branchId}
                                    className="flex flex-col gap-3 rounded-xl bg-default/40 p-4"
                                    {...getNodeTestIdProp(
                                        `branch-comparison-item-${branch.branchId}`,
                                    )}
                                >
                                    <div
                                        className="flex items-center justify-between gap-3"
                                        {...getNodeTestIdProp(
                                            `branch-comparison-item-header-${branch.branchId}`,
                                        )}
                                    >
                                        <div
                                            className="flex min-w-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `branch-comparison-item-label-root-${branch.branchId}`,
                                            )}
                                        >
                                            <span
                                                aria-hidden
                                                className="size-2.5 shrink-0 rounded-sm"
                                                style={{
                                                    backgroundColor:
                                                        branchColor,
                                                }}
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-swatch-${branch.branchId}`,
                                                )}
                                            />
                                            <span
                                                className="truncate text-sm font-medium text-foreground"
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-name-${branch.branchId}`,
                                                )}
                                            >
                                                <RefLabel
                                                    archived={branch.archived}
                                                    deleted={branch.deleted}
                                                    id={branch.branchId}
                                                    name={branch.branchName}
                                                />
                                            </span>
                                        </div>
                                        {/* `formatGel` on the tetri twin, not Intl's currency style: the latter renders `GEL 455` while every other money figure in the app renders `₾ 455` (BOS-171). */}
                                        <span
                                            className="shrink-0 text-sm font-semibold text-foreground tabular-nums"
                                            {...getNodeTestIdProp(
                                                `branch-comparison-item-revenue-${branch.branchId}`,
                                            )}
                                        >
                                            {formatGel(
                                                branch.totalRevenueTetri,
                                            )}
                                        </span>
                                    </div>
                                    {showsShare ? (
                                        <ProgressBar
                                            aria-label={copy.shareAria}
                                            size="lg"
                                            value={percentRounded}
                                            {...getNodeTestIdProp(
                                                `branch-comparison-item-progress-${branch.branchId}`,
                                            )}
                                        >
                                            <ProgressBar.Output
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-progress-output-${branch.branchId}`,
                                                )}
                                            />
                                            <ProgressBar.Track
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-progress-track-${branch.branchId}`,
                                                )}
                                            >
                                                <ProgressBar.Fill
                                                    style={{
                                                        backgroundColor:
                                                            branchColor,
                                                    }}
                                                    {...getNodeTestIdProp(
                                                        `branch-comparison-item-progress-fill-${branch.branchId}`,
                                                    )}
                                                />
                                            </ProgressBar.Track>
                                        </ProgressBar>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
